import { forwardRef } from 'react';
import styled from 'styled-components';
import { Tabs as BaseTabs, TabsProps } from '@mui/material';
import { componentToken, space } from '../../style/utils';

const INDICATOR_HEIGHT = '2px';

type StyledProps = {
  $indicatorBorder?: boolean;
  $allowScroll?: boolean;
};

type Props = TabsProps & {
  indicatorBorder?: boolean;
  allowScroll?: boolean;
};

const StyledTabs = styled(BaseTabs)<StyledProps>`
  width: ${({ orientation }) => (orientation !== 'vertical' ? 'auto' : '100%')};

  &.MuiTabs-root {
    min-height: 0;
  }

  ${({ orientation, theme }) => {
    const { gap } = theme.tokens.components.tabs;
    return orientation !== 'vertical' && gap
      ? `.MuiTabs-flexContainer { gap: ${gap}; }`
      : '';
  }}

  ${({ orientation, theme }) =>
    orientation !== 'vertical' && theme.tokens.components.tabs.hoverUnderline
      ? // In a bar taller than its tabs, the tabs sit on the bottom edge so
        // their hover underline meets the selected indicator.
        `.MuiTabs-flexContainer { height: 100%; align-items: flex-end; }`
      : ''}

  ${({ orientation, theme }) =>
    orientation !== 'vertical' && theme.tokens.components.tabs.indicatorInset
      ? // Hovering the selected tab widens its indicator to the whole tab.
        `&:has(.MuiTab-root.Mui-selected:hover) .MuiTabs-indicator { clip-path: inset(0 round ${INDICATOR_HEIGHT}); }`
      : ''}

  & .MuiTab-root {
    padding: ${({ orientation }) =>
      orientation !== 'vertical'
        ? componentToken('tabs.padding')
        : space('sm', 'lg')};
    margin-left: 0;
    border-left: ${({ theme, $indicatorBorder }) =>
      $indicatorBorder && `2px solid ${theme.palette.border.subtle}`};
    min-height: 0;
    height: ${({ orientation }) => orientation === 'vertical' && '35px'};
    :hover {
      border-radius: ${({ orientation, theme }) =>
        orientation === 'vertical' &&
        theme.tokens.components.tabs.verticalHoverRadius};
    }
  }

  & .MuiTabs-indicator {
    background-color: ${componentToken('tabs.indicator')};
    height: ${INDICATOR_HEIGHT};
    ${({ orientation }) => orientation === 'vertical' && 'left: 0'};
    ${({ orientation, theme }) => {
      const { indicatorInset } = theme.tokens.components.tabs;
      // A padding inset with background-clip would square the rounded ends,
      // and Chrome draws a zero clip inset (the widened indicator) almost
      // square, so the border radius rounds it.
      return orientation !== 'vertical' && indicatorInset
        ? `clip-path: inset(0 ${indicatorInset} round ${INDICATOR_HEIGHT}); border-radius: ${INDICATOR_HEIGHT};`
        : '';
    }}
  }

  & .MuiTabs-scroller {
    ${({ $allowScroll }) => $allowScroll && 'overflow-y: auto !important'};
  }
`;

const Tabs = forwardRef<HTMLDivElement, Props>(
  ({ indicatorBorder, allowScroll, ...props }, ref) => (
    <StyledTabs
      ref={ref}
      $indicatorBorder={indicatorBorder}
      $allowScroll={allowScroll}
      {...props}
    />
  ),
);

export default Tabs;
